import { useState } from "react";
import type { DispatchRecord, PlatformInfo } from "../api";
import { PlatformIcon } from "./PlatformIcon";
import { postBreakdown } from "../lib/analytics";
import { formatCount } from "../lib/metrics";

function whenLabel(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const DAY_MS = 86_400_000;

/** Selectable scopes for the leaderboard: a fixed count, or a rolling window. */
const RANGES = [
  { key: "5", label: "Last 5", kind: "count" as const, n: 5 },
  { key: "7d", label: "7 days", kind: "days" as const, n: 7 },
  { key: "30d", label: "30 days", kind: "days" as const, n: 30 },
];

/**
 * Performance leaderboard: published posts within the chosen scope (last 5, or a
 * 7- / 30-day window), ranked by the engagement they earned (tie-broken by
 * reach), top three medalled, with a running total. The numbers are all real,
 * and each platform icon links to the live post. Dashboard-only; Analytics
 * carries the full per-platform breakdown.
 */
export function RecentPerformance({
  records,
  platforms,
  onViewAnalytics,
}: {
  records: DispatchRecord[];
  platforms: PlatformInfo[];
  onViewAnalytics: () => void;
}) {
  const platformById = new Map(platforms.map((p) => [p.id, p]));
  const [rangeKey, setRangeKey] = useState<string>("5");
  const range = RANGES.find((r) => r.key === rangeKey) ?? RANGES[0]!;

  const sorted = [...records].sort((a, b) => b.at - a.at);
  const inRange =
    range.kind === "count"
      ? sorted.slice(0, range.n)
      : sorted.filter((r) => r.at >= Date.now() - range.n * DAY_MS);

  const ranked = inRange
    .map((rec) => ({ rec, b: postBreakdown(rec, platforms) }))
    .sort(
      (a, b) =>
        b.b.totalEngagement - a.b.totalEngagement ||
        (b.b.totalViews ?? 0) - (a.b.totalViews ?? 0),
    );

  const totalReach = ranked.reduce((n, { b }) => n + (b.totalViews ?? 0), 0);
  const totalEngagement = ranked.reduce((n, { b }) => n + b.totalEngagement, 0);
  const rangeNote =
    range.kind === "count"
      ? `across your last ${ranked.length} post${ranked.length === 1 ? "" : "s"}`
      : `in the last ${range.n} days`;

  return (
    <section className="cc-perf">
      <div className="cc-perf-head">
        <h2>Performance</h2>
        <button type="button" className="link-btn" onClick={onViewAnalytics}>
          View analytics →
        </button>
      </div>

      {records.length === 0 ? (
        <p className="cc-perf-empty">
          Publish your first post and it climbs the board here — ranked by what actually lands.
        </p>
      ) : (
        <>
          <div className="cc-lb-ranges" role="tablist" aria-label="Performance range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                role="tab"
                aria-selected={r.key === rangeKey}
                className={`cc-lb-range${r.key === rangeKey ? " on" : ""}`}
                onClick={() => setRangeKey(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>

          {ranked.length === 0 ? (
            <p className="cc-perf-empty">
              No posts in the last {range.n} days — try a wider range.
            </p>
          ) : (
            <>
              <div className="cc-lb-summary">
                <span>
                  <b>{formatCount(totalReach)}</b> reach
                </span>
                <span className="cc-lb-dot" aria-hidden="true" />
                <span>
                  <b>{formatCount(totalEngagement)}</b> engagement
                </span>
                <span className="cc-lb-note">{rangeNote}</span>
              </div>

              <ol className="cc-lb">
                {ranked.map(({ rec, b }, i) => {
                  const rank = i + 1;
                  const legs = rec.results.filter((r) => r.ok);
                  return (
                    <li key={rec.id} className={`cc-lb-row${rank === 1 ? " is-top" : ""}`}>
                      <span
                        className={`cc-lb-rank is-${rank <= 3 ? rank : "n"}`}
                        aria-label={`Rank ${rank}`}
                      >
                        {rank}
                      </span>
                      <div className="cc-lb-body">
                        <div className="cc-lb-title">{rec.title || "Untitled post"}</div>
                        <div className="cc-lb-when">{whenLabel(rec.at)}</div>
                      </div>
                      <div className="cc-lb-stats">
                        {b.totalViews !== null && (
                          <span className="cc-lb-stat">
                            <b>{formatCount(b.totalViews)}</b> reach
                          </span>
                        )}
                        <span className="cc-lb-stat">
                          <b>{formatCount(b.totalEngagement)}</b> eng
                        </span>
                      </div>
                      <div className="cc-lb-legs">
                        {legs.map((r) => {
                          const p = platformById.get(r.platform);
                          if (!p) return null;
                          const label = p.name;
                          return r.url ? (
                            <a
                              key={r.platform}
                              className="cc-lb-leg"
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Open on ${label}`}
                              aria-label={`Open “${rec.title || "Untitled post"}” on ${label}`}
                            >
                              <PlatformIcon platform={p} size={16} />
                            </a>
                          ) : (
                            <span key={r.platform} className="cc-lb-leg is-static">
                              <PlatformIcon platform={p} size={16} />
                            </span>
                          );
                        })}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </>
      )}
    </section>
  );
}
